import { RootState, useAppDispatch } from '_store';
import applicationReducer from '_store/application/reducer';
import { DialogReturnTarget, DialogType } from '_store/application/types';
import devicesReducer from '_store/devices/devicesReducers';
import { RsuStatusCode } from '_store/devices/devicesTypes';
import { devicesSelector } from '_store/devices/selectors';
import { getRsuStatusFromDiagnostic } from '_store/devices/utils';
import { findDeviceByRsuRef } from '_store/pmv/utils';
import { Typography } from '@material-ui/core';
import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { RsuStatus } from '../../icons';
import { toLatLng } from '../../routes/MapView/utils';
import styles from './styles';

export interface AssociatedRsusProps {
  /** RSU names or station IDs. */
  rsus: string[];
  /** Where closing an RSU's detail goes back to. */
  returnTo: DialogReturnTarget;
}

// The RSUs associated to something, with their status, each opening its detail.
const AssociatedRsus: FC<AssociatedRsusProps> = ({ rsus, returnTo }) => {
  const classes = styles();
  const dispatch = useAppDispatch();
  const devices = useSelector(devicesSelector);
  const diagnostics = useSelector((state: RootState) => state.api.diagnostic);
  const mapRef = useSelector((state: RootState) => state.application.mapRef);

  const associatedRsus = useMemo(
    () =>
      rsus.map((rsuRef) => {
        const deviceMatch = findDeviceByRsuRef(rsuRef, devices);
        const device = deviceMatch?.id
          ? devices.find((d) => d.id === deviceMatch.id)
          : undefined;
        const diagnostic = device ? diagnostics[device.id] : undefined;
        const status =
          getRsuStatusFromDiagnostic(diagnostic) ??
          (device?.alive ? RsuStatusCode.UP : undefined);
        return {
          rsuRef,
          deviceId: device?.id,
          label: device?.name ?? rsuRef,
          status,
        };
      }),
    [devices, diagnostics, rsus],
  );

  const openRsu = (deviceId: string) => {
    const device = devices.find((d) => d.id === deviceId);
    if (device?.location?.coordinates && mapRef) {
      mapRef.flyTo(toLatLng(device.location.coordinates), 15, { animate: true, duration: 1 });
    }

    dispatch(devicesReducer.actions.deviceRequest(deviceId));
    dispatch(
      applicationReducer.actions.infoSelected({
        type: DialogType.SINGLE_DEVICE,
        id: deviceId,
        returnTo,
      }),
    );
  };

  if (associatedRsus.length === 0) {
    return (
      <Typography variant="body2">
        <strong>-</strong>
      </Typography>
    );
  }

  return (
    <ul className={classes.rsuList}>
      {associatedRsus.map(({ rsuRef, deviceId, label, status }) => (
        <li key={rsuRef}>
          {deviceId ? (
            <button
              type="button"
              className={classes.rsuLink}
              onClick={() => openRsu(deviceId)}
            >
              <span className={classes.rsuStatusIcon}>
                <RsuStatus status={status} />
              </span>
              <span className={classes.rsuLinkLabel}>{label}</span>
            </button>
          ) : (
            <span className={classes.rsuText}>{label}</span>
          )}
        </li>
      ))}
    </ul>
  );
};

export default React.memo(AssociatedRsus);
